import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getAuthHeaders, isClerkTokenBridgeActive, clerkSignOut } from '../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'mf_token';
const USE_CLERK = process.env.REACT_APP_USE_CLERK_AUTH === 'true';

const AuthContext = createContext(null);

/**
 * Auth Provider — wraps the app with authentication state.
 * 
 * Step 4: Now Clerk-aware when REACT_APP_USE_CLERK_AUTH=true.
 * 
 * On mount:
 *   1. If USE_CLERK=true: wait briefly for ClerkTokenBridge, then try /api/auth/me with Clerk JWT
 *   2. If USE_CLERK=false or no Clerk session: check localStorage for existing mf_token
 *   3. If found → GET /api/auth/me → set user (or clear if invalid)
 *   4. If not found → POST /api/auth/anonymous → set user + store token
 * 
 * When USE_CLERK=false (default): ALL behavior is unchanged from Step 3.
 * 
 * Provides: user, token, isLoading, isAuthenticated, isAnonymous,
 *           login, signup, logout, onAuthChange, refreshAuth
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);

  // Callback ref for carousel refresh on auth change
  const authChangeListenersRef = useRef([]);

  // MUST be async + await all listeners so that pre-merge flushes
  // (e.g. ensureCarouselPersisted in PostBuilderPage) complete before
  // completeAuth proceeds to the merge-anonymous call.
  const notifyAuthChange = useCallback(async () => {
    const promises = authChangeListenersRef.current.map(fn => {
      try { return Promise.resolve(fn()); } catch (e) { return Promise.resolve(); }
    });
    await Promise.allSettled(promises);
  }, []);

  const onAuthChange = useCallback((listener) => {
    authChangeListenersRef.current.push(listener);
    return () => {
      authChangeListenersRef.current = authChangeListenersRef.current.filter(fn => fn !== listener);
    };
  }, []);

  // Store token in both state and localStorage
  const persistToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setToken(newToken);
  }, []);

  /**
   * Try to validate auth via current headers (supports both Clerk and legacy).
   * Returns { user, token } on success, null on failure.
   */
  const tryAuthValidation = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers['Authorization']) {
        return null;
      }

      const res = await fetch(`${BACKEND_URL}/api/auth/me`, { headers });
      if (res.ok) {
        const data = await res.json();
        return { user: data.user };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    const initAuth = async () => {
      // ── Step 4: Clerk-aware initialization ──────────────────────────
      // When Clerk mode is on, wait briefly for ClerkTokenBridge to inject
      // its token getter, then try to validate an existing Clerk session.
      if (USE_CLERK) {
        // Wait up to 1s for ClerkTokenBridge to be ready (it injects async)
        let waited = 0;
        const waitStep = 100;
        const maxWait = 1000;
        while (!isClerkTokenBridgeActive() && waited < maxWait) {
          await new Promise(r => setTimeout(r, waitStep));
          waited += waitStep;
        }

        if (isClerkTokenBridgeActive()) {
          const clerkResult = await tryAuthValidation();
          if (clerkResult?.user) {
            setUser(clerkResult.user);
            setIsLoading(false);
            return;
          }
          // Clerk bridge active but no session → fall through to legacy path
        }
        // Bridge not active → fall through to legacy path
      }

      // ── Legacy path (active default, or Clerk fallback) ────────────
      const storedToken = localStorage.getItem(TOKEN_KEY);

      if (storedToken) {
        // Validate existing token
        try {
          const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
            setToken(storedToken);
            setIsLoading(false);
            return;
          }
        } catch {
          // Token invalid — fall through to create anonymous
        }
        // Clear invalid token
        localStorage.removeItem(TOKEN_KEY);
      }

      // No valid token — create anonymous session
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/anonymous`, { method: 'POST' });
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          persistToken(data.token);
        }
      } catch (err) {
        console.error('[auth] Failed to create anonymous session:', err);
      }
      setIsLoading(false);
    };

    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Refresh auth state — re-validates current token/session.
   * 
   * Step 4: Called by ClerkEmailCodePanel after Clerk sign-in completes.
   * Uses getAuthHeaders() which automatically picks up the Clerk JWT.
   * Also useful for any post-auth-change refresh.
   * 
   * Returns { success: true } or { success: false, error: string }
   */
  const refreshAuth = useCallback(async () => {
    try {
      const result = await tryAuthValidation();
      if (result?.user) {
        setUser(result.user);
        await notifyAuthChange();
        return { success: true };
      }
      return { success: false, error: 'Session validation failed' };
    } catch (err) {
      return { success: false, error: err.message || 'Refresh failed' };
    }
  }, [tryAuthValidation, notifyAuthChange]);

  /**
   * Log in with email + password.
   * If the current session is anonymous, passes the anonymous_token so the
   * backend can merge anonymous carousels into the real account.
   * Returns { success: true } or { success: false, error: string }
   */
  const login = useCallback(async (email, password) => {
    try {
      const body = { email, password };
      if (user?.is_anonymous && token) {
        body.anonymous_token = token;
      }

      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { success: false, error: data.detail || 'Invalid email or password' };
      }

      const data = await res.json();
      setUser(data.user);
      persistToken(data.token);
      await notifyAuthChange();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, [user, token, persistToken, notifyAuthChange]);

  /**
   * Sign up with name, email, password.
   * If the current session is anonymous, passes the anonymous_token for carousel merge.
   * Returns { success: true } or { success: false, error: string }
   */
  const signup = useCallback(async (name, email, password) => {
    try {
      const body = { name, email, password };
      if (user?.is_anonymous && token) {
        body.anonymous_token = token;
      }

      const res = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 409) {
          return { success: false, error: 'An account with this email already exists' };
        }
        return { success: false, error: data.detail || 'Signup failed' };
      }

      const data = await res.json();
      setUser(data.user);
      persistToken(data.token);
      await notifyAuthChange();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, [user, token, persistToken, notifyAuthChange]);

  /**
   * Log out — cleans up anonymous data on the server, then creates a fresh
   * anonymous session. Triggers carousel refresh.
   * 
   * Step 4 hardening: In Clerk mode, also signs out of Clerk via clerkSignOut().
   * Uses the module-level bridge (not React hooks) — safe to call from context.
   * In legacy mode, clerkSignOut() is a safe no-op (nothing injected).
   */
  const logout = useCallback(async () => {
    // Step 4: Sign out of Clerk first (safe no-op if not in Clerk mode)
    if (USE_CLERK) {
      await clerkSignOut();
    }

    // Clean up anonymous session on the server (deletes anon user + carousels)
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      } catch (err) {
        console.error('[auth] Failed to cleanup session:', err);
      }
    }

    // Clear current session
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setToken(null);

    // Create new anonymous session
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/anonymous`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        persistToken(data.token);
      }
    } catch (err) {
      console.error('[auth] Failed to create anonymous session after logout:', err);
    }

    await notifyAuthChange();
  }, [persistToken, notifyAuthChange]);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !user.is_anonymous,
    isAnonymous: !!user?.is_anonymous,
    isSeed: !!user?.is_seed,
    login,
    signup,
    logout,
    onAuthChange,
    refreshAuth,  // Step 4: for Clerk post-sign-in refresh
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth state and actions.
 * Must be used inside <AuthProvider>.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
